<template>
  <!-- Tutto avvolto da top-bar -->
  <div id="top-bar" class="top-bar">
    <!-- Riga 1: logo + link + logout -->
    <div
      class="top-bar-header"
      :class="{ 'login-layout': $route.path === '/login' }"
    >
      <!-- Logo con link Home -->
      <a
        @click="goToHome"
        role="button"
        class="logo-link"
        :title="$route.path !== '/login' ? 'Clicca per tornare alla home' : ''"
        :aria-label="
          $route.path !== '/login' ? 'Clicca per tornare alla home' : ''
        "
      >
        <img
          id="main-logo"
          src="@/assets/logo-liberotratto.png"
          alt="LiberoTratto CRM"
        />
      </a>

      <!-- Breadcrumb -->
      <div id="breadcrumb-container" v-if="!$route.meta.hideUrlIndex">
        <div id="breadcrumb">
          <template v-for="(crumb, index) in breadcrumbs" :key="index">
            <router-link
              v-if="index < breadcrumbs.length - 1"
              :to="crumb.path"
              class="breadcrumb-link"
            >
              <i v-if="crumb.name === 'Home'" class="fa-solid fa-house"></i>
              <span v-else>{{ crumb.name }}</span>
            </router-link>
            <span v-if="index < breadcrumbs.length - 1"> / </span>
            <span v-else>
              <i v-if="crumb.name === 'Home'" class="fa-solid fa-house"></i>
              <template v-else>{{ crumb.name }}</template>
            </span>
          </template>
        </div>
      </div>

      <!-- Hamburger toggle (solo mobile) -->
      <button
        v-if="$route.path !== '/login'"
        class="hamburger-toggle"
        @click="toggleMenu"
        aria-label="Apri menu"
      >
        <i :class="menuOpen ? 'fa-solid fa-xmark' : 'fa-solid fa-bars'"></i>
      </button>

      <!-- Link a destra -->
      <div
        class="links"
        :class="{ open: menuOpen }"
        v-if="$route.path !== '/login'"
      >
        <!-- Account Link -->
        <button
          v-if="!$route.meta.hideAccountLink && isAdmin"
          @click="goToAccount"
          id="account-button"
          class="account-button"
          :title="
            $route.path !== '/login'
              ? 'Clicca per andare alla pagina Account'
              : ''
          "
          :aria-label="
            $route.path !== '/login'
              ? 'Clicca per andare alla pagina Account'
              : ''
          "
        >
          <i class="fa-solid fa-user"></i>
          Account
        </button>

        <!-- Guida (regola #20) -->
        <button
          id="guide-button"
          @click="openGuide"
          title="Apri la guida operativa"
          aria-label="Apri la guida operativa"
        >
          <i class="fa-solid fa-book"></i>
          {{ $t("guide") || "Guida" }}
        </button>

        <!-- Logout Link -->
        <button
          v-if="!$route.meta.hideLogout"
          id="logout-button"
          @click="logout"
          :disabled="isLoggingOut"
          :title="
            $route.path !== '/login' ? 'Clicca per effettuare il logout' : ''
          "
          :aria-label="
            $route.path !== '/login' ? 'Clicca per effettuare il logout' : ''
          "
        >
          <i
            :class="
              isLoggingOut
                ? 'fa-solid fa-spinner fa-spin'
                : 'fa-solid fa-right-from-bracket'
            "
          ></i>
          {{ isLoggingOut ? "Uscita…" : "Logout" }}
        </button>
      </div>

      <!-- Overlay per chiudere il dropdown -->
      <div v-if="menuOpen" class="menu-overlay" @click="menuOpen = false"></div>
    </div>
  </div>
</template>

<script>
import { setAuth } from "../router";
import { trackEvent, EVENTS } from "@/utilities/analytics";

export default {
  name: "TopBar",
  data() {
    return {
      breadcrumbs: [],
      menuOpen: false,
      isLoggingOut: false,
    };
  },
  computed: {
    isAdmin() {
      return this.$store.state.currentUser?.isAdmin ?? false;
    },
  },
  watch: {
    "$route.path": {
      immediate: true,
      handler() {
        this.breadcrumbs = this.generateBreadcrumbs();
        this.menuOpen = false;
        this.isLoggingOut = false;
      },
    },
  },
  methods: {
    generateBreadcrumbs() {
      const params = this.$route.params;
      return this.$route.matched.map((matchedRoute) => {
        let path = matchedRoute.path;
        for (const [key, value] of Object.entries(params)) {
          path = path.replace(`:${key}`, value);
        }
        let name = matchedRoute.meta.breadcrumb || matchedRoute.name;
        if (matchedRoute.name === "listini-category" && params.category) {
          name =
            params.category.charAt(0).toUpperCase() + params.category.slice(1);
        }
        return { name, path };
      });
    },

    toggleMenu() {
      this.menuOpen = !this.menuOpen;
    },

    goToAccount() {
      this.menuOpen = false;
      if (this.$route.path !== "/login") {
        this.$router.push("/home/account");
      }
      return;
    },

    openGuide() {
      // Solo il nome tecnico della funzione usata.
      trackEvent(EVENTS.FEATURE_USED, { feature: "guide", action: "open" });
      this.menuOpen = false;
      const locale = this.$i18n?.locale === "en" ? "en" : "it";
      const file = locale === "en" ? "manuale_en.html" : "manuale.html";
      const base = process.env.BASE_URL || "/";
      window.open(`${base}guide/${file}`, "_blank", "noopener");
    },

    goToHome() {
      if (this.$route.path !== "/login") {
        this.$router.push("/home");
      }
      return;
    },

    async logout() {
      this.menuOpen = false;

      if (this.$route.path === "/login") {
        return;
      }

      this.isLoggingOut = true;
      try {
        const response = await this.$axios.post("/auth/logout");
        if (response.status === 200) {
          setAuth(false);
          await this.$router.push("/login");
          this.$toast.success("Logout effettuato con successo!");
        }
      } catch (error) {
        this.isLoggingOut = false;
        this.errorMessage = "Errore durante il logout";
        this.$toast.error(this.errorMessage);
        setTimeout(() => {
          this.errorMessage = "";
        }, 2000);
      }
    },
  },
};
</script>

<style src="@/css/App.css"></style>
